import css from './fit-check-explainer.module.css';

/** One pass through all four beats. Each beat gets a quarter of it. */
const BEAT_DURATION = '18.2s';
const EASE = 'cubic-bezier(.22,.9,.28,1)';

function stroke(len: number, width?: number) {
  return { '--len': len, strokeWidth: width } as React.CSSProperties;
}

const HEART_PATH =
  'M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z';

/** One heart, placed by its own group so the animation owns `transform`. */
function Heart({
  x,
  y,
  size,
  className,
}: {
  x: number;
  y: number;
  size: number;
  className?: string;
}) {
  return (
    <g transform={`translate(${x} ${y}) scale(${size})`}>
      <path className={`${css.heart} ${className}`} d={HEART_PATH} />
    </g>
  );
}

/** Beat 1 — a house that draws, then hearts drifting up inside it. */
function HouseArt() {
  return (
    <svg width="118" height="96" viewBox="0 0 118 96" aria-hidden="true">
      <path
        className={`${css.stroke} ${css.draw}`}
        style={stroke(110)}
        d="M20 48 L59 15 L98 48"
      />
      <path
        className={`${css.stroke} ${css.draw}`}
        style={stroke(180)}
        d="M31 44 v40 h56 v-40"
      />
      <Heart x={38} y={58} size={0.85} className={css.h1} />
      <Heart x={50} y={56} size={1.2} className={css.h2} />
      <Heart x={66} y={58} size={0.95} className={css.h3} />
    </svg>
  );
}

/** Beat 2 — a calendar whose rent days stamp green one after another. */
function CalendarArt() {
  return (
    <svg width="118" height="96" viewBox="0 0 118 96" aria-hidden="true">
      <path
        className={`${css.stroke} ${css.draw}`}
        style={stroke(60, 3.6)}
        d="M38 12 v12 M80 12 v12"
      />
      <rect
        className={`${css.stroke} ${css.draw}`}
        style={stroke(300)}
        x="20"
        y="20"
        width="78"
        height="64"
        rx="8"
      />
      <path
        className={`${css.stroke} ${css.draw}`}
        style={stroke(80, 3.4)}
        d="M20 40 H98"
      />
      <rect
        className={`${css.cell} ${css.c1}`}
        x="32"
        y="52"
        width="14"
        height="12"
        rx="3"
      />
      <rect
        className={`${css.cell} ${css.c2}`}
        x="52"
        y="52"
        width="14"
        height="12"
        rx="3"
      />
      <rect
        className={`${css.cell} ${css.c3}`}
        x="72"
        y="52"
        width="14"
        height="12"
        rx="3"
      />
    </svg>
  );
}

/** Beat 4 — an applicant scanned inside a frame, then cleared with a check. */
function ScanArt() {
  return (
    <svg width="118" height="96" viewBox="0 0 118 96" aria-hidden="true">
      <rect
        className={`${css.stroke} ${css.draw}`}
        style={stroke(285)}
        x="26"
        y="12"
        width="66"
        height="72"
        rx="9"
      />
      <circle
        className={`${css.stroke} ${css.draw}`}
        style={stroke(74, 3.6)}
        cx="59"
        cy="40"
        r="11"
      />
      <path
        className={`${css.stroke} ${css.draw}`}
        style={stroke(66, 3.6)}
        d="M40 72 a19 19 0 0 1 38 0"
      />
      <path className={css.scan} d="M33 24 H85" />
      <g className={css.cleared}>
        <circle cx="90" cy="76" r="13" />
        <path d="M84 76.5 l4 4 L96 72.5" />
      </g>
    </svg>
  );
}

type SceneProps = {
  className?: string;
  art: React.ReactNode;
  kicker: string;
  title: string;
  body: string;
};

function Scene({ className, art, kicker, title, body }: SceneProps) {
  return (
    <div className={`${css.scene} ${className}`}>
      <div className={css.art}>{art}</div>
      <div className={css.cap}>
        <p className={css.kicker}>{kicker}</p>
        <h3>{title}</h3>
        <p className={css.body}>{body}</p>
      </div>
    </div>
  );
}

/**
 * Looping explainer shown above the Quick Fit Check form: it tells residents
 * why we ask for income and credit before they answer.
 *
 * Decorative — the form below carries the real content, so the whole stage is
 * hidden from assistive technology rather than narrated beat by beat.
 */
export function FitCheckExplainer() {
  return (
    <div
      className={css.stage}
      aria-hidden="true"
      style={{ '--beat': BEAT_DURATION, '--ease': EASE } as React.CSSProperties}
    >
      <div className={css.wash}>
        <b className={css.w1} />
        <b className={css.w2} />
        <b className={css.w3} />
      </div>

      <Scene
        className={css.s1}
        kicker="Why we ask"
        title="This is someone's home."
        body="We look after it the way they would."
        art={<HouseArt />}
      />

      <Scene
        className={css.s2}
        kicker="The promise"
        title="Their rent, guaranteed."
        body="Paid every month. That's on us, not on you."
        art={<CalendarArt />}
      />

      <Scene
        className={css.s3}
        kicker="The bar"
        title="So we look for two things."
        body="700+ credit, and income that covers the rent."
        art={
          <div className={css.meter}>
            <div>
              <div className={`${css.lbl} ${css.l1}`}>
                <span>Credit</span>
                <span>700+</span>
              </div>
              <div className={css.track}>
                <div className={`${css.fill} ${css.f1}`} />
              </div>
            </div>
            <div>
              <div className={`${css.lbl} ${css.l2}`}>
                <span>Income</span>
                <span>covers rent</span>
              </div>
              <div className={css.track}>
                <div className={`${css.fill} ${css.f2}`} />
              </div>
            </div>
          </div>
        }
      />

      <Scene
        className={css.s4}
        kicker="Two minutes"
        title="Let's see if it's a match."
        body="Before you spend time on the wrong home."
        art={<ScanArt />}
      />
    </div>
  );
}
